import React, { useEffect, useMemo } from 'react';
import { shallowEqual, useParams, useSelector } from 'umi';
import { ILogConfigType } from '../type';
import MarqueeInfo from '../../MarqueeInfo';
import { buriedPoint } from '@/pages/home/utils/buriedPoint';
import Render from '@/pages/dashboard/components/PageContainer/Render';
import { useProps } from '../Render/hooks';
import { dynamicString } from '@home/utils/dynamicString';
import {
  IMetricConfig,
  IContentWrapProps,
  IDashboardState,
  IContextObj,
  IRenderProps,
} from './type';

/**
 * CommonContentWrap 组件
 * 处理跑马灯通知、打点逻辑和子组件渲染
 *
 * @param props - 组件属性
 * @returns React组件
 */
const CommonContentWrap = (props: IContentWrapProps): React.ReactElement => {
  const { config: content, children = [] } = props;
  const { data } = useProps({ ...props, config: content });
  const { isHaveNotification, logConfig, notificationPath } = content || {};
  const { metric: metricKey } = useParams<{ metric: string; }>();
  const { currentMenu } = useSelector((state: IDashboardState) => ({
    currentMenu: state?.dashboard?.currentMenu,
  }), shallowEqual);

  /**
   * 获取打点的相关变量信息
   * 从指标配置中提取指标的英文名和中文名
   */
  const contextObj = useMemo((): IContextObj => {
    const metricConfig = data?.metricConfig as IMetricConfig | undefined;
    const metricInfo = metricConfig?.[metricKey];
    const metricNameEn = metricInfo?.nameEn || '';
    const metricNameCn = metricInfo?.nameCn || '';
    const context: IContextObj = {
      metricKey: metricKey || '',
      metricNameEn,
      metricNameCn,
    };
    return context;
  }, [data, metricKey]);

  /**
   * 获取打点extInfo信息
   * 支持字符串类型直接返回，对象类型进行动态字符串替换后序列化
   *
   * @param extInfo - 额外信息，可以是字符串或对象
   * @returns 处理后的额外信息字符串
   */
  const getExtInfo = (extInfo: string | { [key: string]: string; }): string => {
    if (typeof extInfo === 'string') {
      return extInfo;
    }
    const result: { [key: string]: string; } = {};
    const keys = Object.keys(extInfo);
    for (const key of keys) {
      result[key] = dynamicString(extInfo[key], { [key]: contextObj?.[key] });
    }
    return JSON.stringify(result);
  };

  /**
   * 页面打点副作用
   * 当content配置变化时触发打点，记录页面访问信息
   */
  useEffect(() => {
    if (!logConfig) {
      return;
    }

    const {
      pageId,
      pageName,
      extInfo,
      pagePath,
      typeId,
      uidType,
      ...otherLogConfig
    } = logConfig as ILogConfigType & { [key: string]: unknown; };

    const gameCode = data?.gameCode;

    const logObj = {
      menuId: currentMenu?.menu_id,
      pageId,
      pageName,
      extInfo: getExtInfo(extInfo),
      pagePath,
      uid: typeof gameCode === 'string' ? gameCode : '',
      typeId,
      uidType,
      ...otherLogConfig,
    };
    buriedPoint(logObj);
  }, [content]);

  return (
    <>
      {isHaveNotification && <MarqueeInfo pagePath={notificationPath} />}
      {children?.map((item, index) => {
        const { config, data: itemData, changeData } = item?.props || {};
        const renderProps: IRenderProps = {
          config,
          logConfig,
          data: itemData,
          changeData,
        };
        return (
          <React.Fragment key={`${item?.name || 'child'}-${index}`}>
            <Render {...renderProps} />
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CommonContentWrap;
